import { type SiteConfig } from "./AdminDataContext";

const FONT_OPTIONS = [
  "Cormorant Garamond",
  "Jost",
  "Inter",
  "Playfair Display",
  "DM Serif Display",
  "Lora",
  "Source Sans 3",
  "Noto Sans JP",
  "Noto Serif JP",
  "Raleway",
  "Poppins",
  "Libre Baskerville",
];

const RADIUS_OPTIONS = [
  { value: "full", label: "Pill (full)" },
  { value: "lg", label: "Large (12px)" },
  { value: "md", label: "Medium (8px)" },
  { value: "sm", label: "Small (4px)" },
  { value: "none", label: "Square (0px)" },
];

function ColorInput({ label, value, onChange }: { label: string; value: string; onChange: (v: string) => void }) {
  return (
    <div className="flex items-center gap-3">
      <div className="relative">
        <input
          type="color"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-9 h-9 rounded-lg cursor-pointer border border-[#E6E0D7] p-0.5"
        />
      </div>
      <div className="flex-1 min-w-0">
        <label className="block text-[#6F675F] tracking-[0.12em] uppercase mb-1" style={{ fontSize: "10px" }}>
          {label}
        </label>
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          maxLength={30}
          className="w-full px-2.5 py-1.5 bg-[#F7F5F1] border border-[#E6E0D7] rounded-lg text-[#2D2A26] focus:outline-none focus:border-[#C7A76C]"
          style={{ fontSize: "12px", fontFamily: "'Jost', sans-serif" }}
        />
      </div>
    </div>
  );
}

function SelectInput({ label, value, onChange, options }: { label: string; value: string; onChange: (v: string) => void; options: { value: string; label: string }[] }) {
  return (
    <div>
      <label className="block text-[#6F675F] tracking-[0.12em] uppercase mb-2" style={{ fontSize: "10px" }}>{label}</label>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full px-3 py-2.5 bg-[#F7F5F1] border border-[#E6E0D7] rounded-lg text-[#2D2A26] focus:outline-none focus:border-[#C7A76C]"
        style={{ fontSize: "13px", fontFamily: "'Jost', sans-serif" }}
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
    </div>
  );
}

export function AdminThemeTab({
  config,
  setConfig,
}: {
  config: SiteConfig;
  setConfig: (c: SiteConfig) => void;
}) {
  const theme = config.theme || {
    colors: { background: "#F7F5F1", foreground: "#2D2A26", primary: "#6F7F63", accent: "#C7A76C", muted: "#6F675F", border: "#E6E0D7", card: "#FFFFFF" },
    navbar: { background: "#F7F5F1", scrolledBackground: "rgba(247,245,241,0.95)", textColor: "#2D2A26" },
    buttons: { primaryBg: "#6F7F63", primaryText: "#FFFFFF", borderRadius: "full" as const },
    fonts: { heading: "Cormorant Garamond", body: "Jost" },
  };

  const updateColors = (key: string, value: string) => {
    setConfig({
      ...config,
      theme: { ...theme, colors: { ...theme.colors, [key]: value } },
    });
  };

  const updateNavbar = (key: string, value: string) => {
    setConfig({
      ...config,
      theme: { ...theme, navbar: { ...theme.navbar, [key]: value } },
    });
  };

  const updateButtons = (key: string, value: string) => {
    setConfig({
      ...config,
      theme: { ...theme, buttons: { ...theme.buttons, [key]: value } },
    });
  };

  const updateFonts = (key: string, value: string) => {
    setConfig({
      ...config,
      theme: { ...theme, fonts: { ...theme.fonts, [key]: value } },
    });
  };

  const resetDefaults = () => {
    setConfig({
      ...config,
      theme: {
        colors: { background: "#F7F5F1", foreground: "#2D2A26", primary: "#6F7F63", accent: "#C7A76C", muted: "#6F675F", border: "#E6E0D7", card: "#FFFFFF" },
        navbar: { background: "#F7F5F1", scrolledBackground: "rgba(247,245,241,0.95)", textColor: "#2D2A26" },
        buttons: { primaryBg: "#6F7F63", primaryText: "#FFFFFF", borderRadius: "full" },
        fonts: { heading: "Cormorant Garamond", body: "Jost" },
      },
    });
  };

  const btnRadius = theme.buttons.borderRadius === "full" ? "9999px" : theme.buttons.borderRadius === "lg" ? "12px" : theme.buttons.borderRadius === "md" ? "8px" : theme.buttons.borderRadius === "sm" ? "4px" : "0px";

  return (
    <div className="space-y-6">
      {/* Colours */}
      <div className="bg-white rounded-xl p-6 shadow-[0_2px_16px_rgba(40,32,20,0.04)]">
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-[#2D2A26] tracking-[0.08em]" style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "20px", fontWeight: 400 }}>
            Website Colours
          </h2>
          <button
            onClick={resetDefaults}
            className="text-[#6F675F] hover:text-[#8B5C58] transition-colors tracking-[0.1em] uppercase border border-[#E6E0D7] rounded-lg px-4 py-2 hover:border-[#8B5C58]/30"
            style={{ fontSize: "10px" }}
          >
            Reset to MOECHA Defaults
          </button>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          <ColorInput label="Background" value={theme.colors.background} onChange={(v) => updateColors("background", v)} />
          <ColorInput label="Foreground (Text)" value={theme.colors.foreground} onChange={(v) => updateColors("foreground", v)} />
          <ColorInput label="Primary" value={theme.colors.primary} onChange={(v) => updateColors("primary", v)} />
          <ColorInput label="Accent" value={theme.colors.accent} onChange={(v) => updateColors("accent", v)} />
          <ColorInput label="Muted Text" value={theme.colors.muted} onChange={(v) => updateColors("muted", v)} />
          <ColorInput label="Border" value={theme.colors.border} onChange={(v) => updateColors("border", v)} />
          <ColorInput label="Card / Surface" value={theme.colors.card} onChange={(v) => updateColors("card", v)} />
        </div>
      </div>

      {/* Navbar */}
      <div className="bg-white rounded-xl p-6 shadow-[0_2px_16px_rgba(40,32,20,0.04)]">
        <h2 className="text-[#2D2A26] tracking-[0.08em] mb-5" style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "20px", fontWeight: 400 }}>
          Navigation Bar
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          <ColorInput label="Nav Background" value={theme.navbar.background} onChange={(v) => updateNavbar("background", v)} />
          <ColorInput label="Nav Text" value={theme.navbar.textColor} onChange={(v) => updateNavbar("textColor", v)} />
          <div>
            <label className="block text-[#6F675F] tracking-[0.12em] uppercase mb-2" style={{ fontSize: "10px" }}>Scrolled Background</label>
            <input
              type="text"
              value={theme.navbar.scrolledBackground}
              onChange={(e) => updateNavbar("scrolledBackground", e.target.value)}
              maxLength={60}
              className="w-full px-3 py-2.5 bg-[#F7F5F1] border border-[#E6E0D7] rounded-lg text-[#2D2A26] focus:outline-none focus:border-[#C7A76C]"
              style={{ fontSize: "12px" }}
              placeholder="rgba(247,245,241,0.95)"
            />
          </div>
        </div>
        {/* Preview strip */}
        <div className="mt-5">
          <p className="text-[#D8D1C7] tracking-[0.12em] uppercase mb-2" style={{ fontSize: "9px" }}>Preview</p>
          <div
            className="rounded-xl px-8 py-4 flex items-center justify-between border border-[#E6E0D7]"
            style={{ backgroundColor: theme.navbar.background }}
          >
            <span style={{ color: theme.navbar.textColor, fontFamily: "'Jost', sans-serif", fontSize: "12px", letterSpacing: "0.15em", textTransform: "uppercase" }}>Shop</span>
            <span style={{ color: theme.navbar.textColor, fontFamily: "'Jost', sans-serif", fontSize: "12px", letterSpacing: "0.15em", textTransform: "uppercase" }}>MOECHA</span>
            <span style={{ color: theme.navbar.textColor, fontFamily: "'Jost', sans-serif", fontSize: "12px", letterSpacing: "0.15em", textTransform: "uppercase" }}>Cart</span>
          </div>
        </div>
      </div>

      {/* Buttons */}
      <div className="bg-white rounded-xl p-6 shadow-[0_2px_16px_rgba(40,32,20,0.04)]">
        <h2 className="text-[#2D2A26] tracking-[0.08em] mb-5" style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "20px", fontWeight: 400 }}>
          Buttons
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          <ColorInput label="Button Background" value={theme.buttons.primaryBg} onChange={(v) => updateButtons("primaryBg", v)} />
          <ColorInput label="Button Text" value={theme.buttons.primaryText} onChange={(v) => updateButtons("primaryText", v)} />
          <SelectInput
            label="Border Radius"
            value={theme.buttons.borderRadius}
            onChange={(v) => updateButtons("borderRadius", v)}
            options={RADIUS_OPTIONS}
          />
        </div>
        <div className="mt-5">
          <p className="text-[#D8D1C7] tracking-[0.12em] uppercase mb-2" style={{ fontSize: "9px" }}>Preview</p>
          <div className="flex gap-4 items-center">
            <button
              className="px-6 py-3 tracking-[0.2em] uppercase transition-opacity"
              style={{
                backgroundColor: theme.buttons.primaryBg,
                color: theme.buttons.primaryText,
                borderRadius: btnRadius,
                fontFamily: "'Jost', sans-serif",
                fontSize: "12px",
              }}
            >
              Add to Cart
            </button>
            <button
              className="px-6 py-3 border tracking-[0.2em] uppercase transition-opacity"
              style={{
                borderColor: theme.buttons.primaryBg,
                color: theme.buttons.primaryBg,
                borderRadius: btnRadius,
                fontFamily: "'Jost', sans-serif",
                fontSize: "12px",
              }}
            >
              Learn More
            </button>
          </div>
        </div>
      </div>

      {/* Fonts */}
      <div className="bg-white rounded-xl p-6 shadow-[0_2px_16px_rgba(40,32,20,0.04)]">
        <h2 className="text-[#2D2A26] tracking-[0.08em] mb-5" style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "20px", fontWeight: 400 }}>
          Typography
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <SelectInput
            label="Heading Font"
            value={theme.fonts.heading}
            onChange={(v) => updateFonts("heading", v)}
            options={FONT_OPTIONS.map((f) => ({ value: f, label: f }))}
          />
          <SelectInput
            label="Body Font"
            value={theme.fonts.body}
            onChange={(v) => updateFonts("body", v)}
            options={FONT_OPTIONS.map((f) => ({ value: f, label: f }))}
          />
        </div>
        <div className="mt-5 p-4 bg-[#F7F5F1] rounded-lg">
          <p className="text-[#D8D1C7] tracking-[0.12em] uppercase mb-3" style={{ fontSize: "9px" }}>Preview</p>
          <h3
            className="text-[#2D2A26] mb-2"
            style={{ fontFamily: `'${theme.fonts.heading}', serif`, fontSize: "24px", fontWeight: 400 }}
          >
            The Art of Matcha
          </h3>
          <p
            className="text-[#6F675F]"
            style={{ fontFamily: `'${theme.fonts.body}', sans-serif`, fontSize: "14px", fontWeight: 300 }}
          >
            Each gesture is intentional: the warming of the bowl, the gentle whisking, the moment of pause before the first sip.
          </p>
        </div>
      </div>

      {/* Note */}
      <div className="flex items-start gap-3 bg-[#6F7F63]/5 border border-[#6F7F63]/15 rounded-lg px-4 py-3">
        <div className="shrink-0 w-5 h-5 rounded-full bg-[#6F7F63]/10 flex items-center justify-center mt-0.5">
          <span className="text-[#6F7F63]" style={{ fontSize: "12px" }}>i</span>
        </div>
        <div>
          <p className="text-[#2D2A26] mb-0.5" style={{ fontSize: "12px" }}>
            Theme changes apply instantly after saving
          </p>
          <p className="text-[#6F675F]" style={{ fontSize: "11px", fontWeight: 300 }}>
            CSS custom properties are injected into the page at runtime. Many storefront components reference the admin theme via the <code>useTheme()</code> hook.
            Components that still use hardcoded hex colors will be migrated in a future update.
          </p>
        </div>
      </div>
    </div>
  );
}